import { gameAPI, buildGameWsUrl } from '../api/gameApi';

/**
 * Native WebSocket client of the real-time game server (SPEC_V141_GAMES.md §3).
 *  - one-off ticket from POST /v1/game/ws-ticket (JWT never goes into the URL);
 *  - ping every 2 s → RTT + server clock offset (best of the last samples);
 *  - one automatic reconnect (with a fresh ticket) on an unexpected close; the last
 *    `queue.join` / `pve.join` is re-sent after it (cleared on `match` for PvP);
 *  - held inputs are sent only on change (`in`), presses as `act`;
 *  - tiny event emitter: on(type, fn) where type is a server message `t`
 *    or one of 'open' | 'close' | 'reconnecting' | 'reconnected' | 'failed' | 'status' | 'message'.
 */

const CLOSE_NORMAL = 1000;

export default class GameSocket {
  constructor({
    getTicket = gameAPI.getWsTicket,
    buildUrl = buildGameWsUrl,
    WebSocketImpl = typeof WebSocket !== 'undefined' ? WebSocket : null,
    pingIntervalMs = 2000,
    maxReconnects = 1,
    now = () => Date.now(),
  } = {}) {
    this.getTicket = getTicket;
    this.buildUrl = buildUrl;
    this.WebSocketImpl = WebSocketImpl;
    this.pingIntervalMs = pingIntervalMs;
    this.maxReconnects = maxReconnects;
    this.now = now;

    this.ws = null;
    this.listeners = new Map();
    this.status = 'idle'; // idle | connecting | open | reconnecting | closed | failed
    this.reconnects = 0;
    this.closedByUser = false;
    this.pingTimer = null;
    this.seq = 0;
    this.lastInput = null;
    this.heldInput = { l: 0, r: 0, b: 0 };
    this.resumeMsg = null;
    this.rtt = null;
    this.clockOffset = 0; // serverMs − localMs
    this.samples = [];
  }

  // ── emitter ────────────────────────────────────────────────────────────────
  on(type, fn) {
    if (!this.listeners.has(type)) this.listeners.set(type, new Set());
    this.listeners.get(type).add(fn);
    return () => this.off(type, fn);
  }

  off(type, fn) {
    const set = this.listeners.get(type);
    if (set) set.delete(fn);
  }

  emit(type, payload) {
    const set = this.listeners.get(type);
    if (!set) return;
    [...set].forEach((fn) => {
      try { fn(payload); } catch (e) { /* listener errors must not kill the socket */ console.error(e); }
    });
  }

  setStatus(status) {
    if (this.status === status) return;
    this.status = status;
    this.emit('status', status);
  }

  // ── connection ─────────────────────────────────────────────────────────────
  async connect() {
    if (this.ws && (this.status === 'open' || this.status === 'connecting')) return;
    this.closedByUser = false;
    this.setStatus(this.reconnects > 0 ? 'reconnecting' : 'connecting');
    let ticket;
    try {
      const res = await this.getTicket();
      ticket = typeof res === 'string' ? res : res?.ticket;
      if (!ticket) throw new Error('No ticket');
    } catch (e) {
      if (this.closedByUser) return;
      this.setStatus('failed');
      this.emit('failed', { reason: 'ticket', error: e });
      return;
    }
    if (this.closedByUser) return;
    this.openSocket(this.buildUrl(ticket));
  }

  openSocket(url) {
    if (!this.WebSocketImpl) {
      this.setStatus('failed');
      this.emit('failed', { reason: 'unsupported' });
      return;
    }
    const ws = new this.WebSocketImpl(url);
    this.ws = ws;
    ws.onopen = () => {
      if (this.ws !== ws) return;
      const wasReconnect = this.reconnects > 0;
      this.setStatus('open');
      this.lastInput = null;
      this.startPing();
      this.emit('open', { reconnect: wasReconnect });
      if (wasReconnect) {
        if (this.resumeMsg) this.send(this.resumeMsg);
        this.sendInput(this.heldInput);
        this.emit('reconnected');
      }
    };
    ws.onmessage = (ev) => {
      if (this.ws !== ws) return;
      let msg;
      try { msg = JSON.parse(ev.data); } catch (e) { return; }
      if (!msg || typeof msg !== 'object') return;
      if (msg.t === 'pong') this.handlePong(msg);
      if (msg.t === 'match' && msg.mode !== 'PVE') this.resumeMsg = null;
      // after a reconnect the host gets the same invite room back
      if (msg.t === 'room.created' && this.resumeMsg?.t === 'room.create') this.resumeMsg = { ...this.resumeMsg, code: msg.code };
      this.emit('message', msg);
      if (msg.t) this.emit(msg.t, msg);
    };
    ws.onerror = () => { /* followed by onclose */ };
    ws.onclose = (ev) => {
      if (this.ws !== ws) return;
      this.stopPing();
      this.ws = null;
      if (this.closedByUser || ev?.code === CLOSE_NORMAL) {
        this.setStatus('closed');
        this.emit('close', { code: ev?.code, reason: ev?.reason });
        return;
      }
      if (this.reconnects < this.maxReconnects) {
        this.reconnects += 1;
        this.setStatus('reconnecting');
        this.emit('reconnecting', { code: ev?.code });
        this.connect();
      } else {
        this.setStatus('failed');
        this.emit('failed', { reason: 'closed', code: ev?.code });
      }
    };
  }

  close() {
    this.closedByUser = true;
    this.stopPing();
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      try { ws.close(CLOSE_NORMAL); } catch (e) { /* ignore */ }
    }
    this.setStatus('closed');
  }

  isOpen() {
    const OPEN = this.WebSocketImpl?.OPEN ?? 1;
    return !!this.ws && this.ws.readyState === OPEN;
  }

  send(obj) {
    if (!this.isOpen()) return false;
    try {
      this.ws.send(JSON.stringify(obj));
      return true;
    } catch (e) {
      return false;
    }
  }

  // ── ping / clock ───────────────────────────────────────────────────────────
  startPing() {
    this.stopPing();
    this.ping();
    this.pingTimer = setInterval(() => this.ping(), this.pingIntervalMs);
  }

  stopPing() {
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = null;
  }

  ping() {
    this.send({ t: 'ping', c: this.now() });
  }

  handlePong({ c, s }) {
    const now = this.now();
    const rtt = now - Number(c);
    if (!Number.isFinite(rtt) || rtt < 0) return;
    const offset = Number.isFinite(Number(s)) ? Number(s) + rtt / 2 - now : this.clockOffset;
    this.samples.push({ rtt, offset });
    if (this.samples.length > 6) this.samples.shift();
    // the lowest-RTT sample has the most accurate offset
    const best = this.samples.reduce((m, x) => (x.rtt < m.rtt ? x : m), this.samples[0]);
    this.clockOffset = best.offset;
    this.rtt = this.rtt === null ? rtt : this.rtt * 0.7 + rtt * 0.3;
    this.emit('rtt', { rtt: this.rtt, offset: this.clockOffset });
  }

  serverNow() {
    return this.now() + this.clockOffset;
  }

  // ── game messages ──────────────────────────────────────────────────────────
  nextSeq() {
    this.seq += 1;
    return this.seq;
  }

  /** Held buttons {l, r, b}; sent only when they differ from the last sent state. */
  sendInput({ l = 0, r = 0, b = 0 } = {}) {
    const input = { l: l ? 1 : 0, r: r ? 1 : 0, b: b ? 1 : 0 };
    this.heldInput = input;
    const last = this.lastInput;
    if (last && last.l === input.l && last.r === input.r && last.b === input.b) return false;
    const sent = this.send({ t: 'in', q: this.nextSeq(), ...input });
    if (sent) this.lastInput = input;
    return sent;
  }

  /** Press: 'atk' | 'cast'. */
  sendAct(a) {
    return this.send({ t: 'act', q: this.nextSeq(), a });
  }

  joinQueue(characterId, stake) {
    const msg = { t: 'queue.join', characterId, stake: String(stake ?? '0') };
    this.resumeMsg = msg;
    return this.send(msg);
  }

  leaveQueue() {
    this.resumeMsg = null;
    return this.send({ t: 'queue.leave' });
  }

  /** «Play with a friend»: open an invite room (→ `room.created {code}`). */
  createRoom(characterId, stake) {
    const msg = { t: 'room.create', characterId, stake: String(stake ?? '0') };
    this.resumeMsg = msg;
    return this.send(msg);
  }

  joinRoom(code, characterId) {
    const msg = { t: 'room.join', code, characterId };
    this.resumeMsg = msg;
    return this.send(msg);
  }

  cancelRoom() {
    this.resumeMsg = null;
    return this.send({ t: 'room.cancel' });
  }

  joinPve(runId) {
    const msg = { t: 'pve.join', runId };
    this.resumeMsg = msg;
    return this.send(msg);
  }

  leave() {
    this.resumeMsg = null;
    return this.send({ t: 'leave' });
  }
}
